import { useState } from 'react';
import { Play } from 'lucide-react';

import { MediaFrame, ScrollReveal, SplitReveal } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { cn } from '@ibas/ui';

/** YouTube id for the IBAS graduates film — facade-loaded on user click. */
const YOUTUBE_ID = 'QUewRofimA4';

interface GalleryImage {
  /** Site-relative path under /media (leading slash). */
  path: string;
  /** Meaningful alt text — describes the photo, not an institutional claim. */
  alt: string;
  /** CSS object-position keeping faces in frame under object-cover. */
  position: string;
}

/**
 * Supporting imagery shown beside the film — real IBAS graduation moments.
 * The gallery frames are tall (aspect-[3/4]), so each slot uses a portrait or
 * strongly vertical graduate photo to sit well under object-cover.
 */
const GALLERY: readonly GalleryImage[] = [
  {
    path: '/media/images/graduation-events/graduation-dba-sash-portrait.webp',
    alt: 'An IBAS DBA graduate in cap and gown having a gold DBA sash placed over his shoulders at the graduation ceremony.',
    position: '50% 30%',
  },
  {
    path: '/media/images/graduation-events/graduation-procession-red-carpet-mba.webp',
    alt: 'An IBAS graduate in cap and gown with MBA sashes walking down a red carpet during the graduation ceremony.',
    position: '50% 35%',
  },
  {
    path: '/media/images/graduation-events/graduate-portrait-cap-gown.webp',
    alt: 'Close-up portrait of an IBAS graduate wearing a cap and gown at a graduation ceremony.',
    position: '50% 25%',
  },
  {
    path: '/media/images/graduation-events/dba-graduate-portrait-ceremony-backdrop.webp',
    alt: 'Portrait of an IBAS DBA graduate in cap and gown with a gold sash, before the graduation ceremony backdrop.',
    position: '50% 30%',
  },
];

export interface GraduatesShowcaseProps {
  className?: string;
}

/**
 * GraduatesShowcase — "Our graduates": a click-to-play YouTube film paired with
 * a small column of graduation imagery.
 *
 * Motion intent: the header and media rise and settle on scroll-in via the house
 * ScrollReveal (with the heading masked-revealed by SplitReveal), and each
 * gallery image plays MediaFrame's curtain-wipe as it enters view. The film uses
 * a lightweight facade — a real poster <button> over the YouTube thumbnail with a
 * gold play surface that gently pulses to invite the click; the heavy iframe is
 * only mounted after the user presses play (user-initiated autoplay), so nothing
 * loads or moves on its own. Under reduced motion the reveals resolve to their
 * static visible end-state, the gallery wipes are skipped, and the play button's
 * pulse is disabled — the section reads as a calm, fully legible still.
 */
export function GraduatesShowcase({ className }: GraduatesShowcaseProps) {
  const reduced = useReducedMotion();
  const [playing, setPlaying] = useState(false);

  return (
    <section
      aria-labelledby="graduates-showcase-heading"
      className={cn(
        'flex min-h-[100svh] flex-col justify-center overflow-hidden bg-background px-6 py-12 lg:px-16',
        className,
      )}
    >
      <div className="mx-auto w-full max-w-6xl">
        <ScrollReveal className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Our graduates
          </p>
          <h2 id="graduates-showcase-heading">
            <SplitReveal
              as="span"
              by="words"
              className="mt-3 block font-display text-3xl leading-tight font-semibold text-foreground sm:text-4xl md:text-5xl"
            >
              Meet our graduates
            </SplitReveal>
          </h2>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            A short film featuring IBAS graduates, alongside moments from across the graduate
            community.
          </p>
        </ScrollReveal>

        <div className="mt-8 grid gap-6 lg:mt-10 lg:grid-cols-[1fr_minmax(0,320px)] lg:gap-8">
          {/* Film — facade poster until the viewer presses play. */}
          <ScrollReveal>
            <div className="relative aspect-video overflow-hidden rounded-2xl bg-brand-navy shadow-lg">
              {playing ? (
                /* Trusted cross-origin YouTube embed. A sandbox is intentionally
                   omitted: YouTube's player needs both allow-scripts and
                   allow-same-origin, a combination that provides no real
                   isolation for a third-party frame anyway. */
                <iframe
                  src={`https://www.youtube.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0`}
                  title="IBAS graduates film"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  className="absolute inset-0 h-full w-full"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  aria-label="Play the IBAS graduates film"
                  className="group absolute inset-0 h-full w-full cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <img
                    src={`https://img.youtube.com/vi/${YOUTUBE_ID}/sddefault.jpg`}
                    alt="IBAS graduates film — thumbnail"
                    loading="lazy"
                    // YouTube serves a tiny 120×90 grey placeholder (not a 404)
                    // when a size is missing, so `onError` won't catch it — detect
                    // the placeholder by its width on load and step down to a size
                    // that always holds a real frame (hqdefault).
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      if (img.naturalWidth <= 120 && !img.dataset.fallback) {
                        img.dataset.fallback = '1';
                        img.src = `https://img.youtube.com/vi/${YOUTUBE_ID}/hqdefault.jpg`;
                      }
                    }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  {/* Subtle navy overlay so the poster reads as a play surface. */}
                  <span
                    aria-hidden
                    className="absolute inset-0 bg-brand-navy/40 transition-colors duration-300 ease-out group-hover:bg-brand-navy/25"
                  />
                  {/* Gold circular play button — gold bg + navy icon (button rule). */}
                  <span
                    aria-hidden
                    className="absolute top-1/2 left-1/2 flex size-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-brand-gold text-brand-navy shadow-xl transition-transform duration-300 ease-out group-hover:scale-105 sm:size-24"
                  >
                    {/* Inviting pulse ring — disabled under reduced motion. */}
                    <span
                      aria-hidden
                      className={cn(
                        'absolute inset-0 rounded-full bg-brand-gold/60',
                        !reduced && 'animate-ping',
                      )}
                    />
                    <Play
                      className="relative ml-1 size-8 sm:size-9"
                      fill="currentColor"
                      strokeWidth={0}
                    />
                  </span>
                </button>
              )}
            </div>
          </ScrollReveal>

          {/* Gallery — a 2×2 grid of graduation imagery beside the film. */}
          <ScrollReveal stagger={0.1} className="grid grid-cols-2 gap-4">
            {GALLERY.map((photo) => (
              <MediaFrame
                key={photo.path}
                image={photo.path}
                alt={photo.alt}
                aspect="aspect-[3/4]"
                rounded="rounded-xl"
                position={photo.position}
              />
            ))}
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
